import sanitizeHtmlLibrary from 'sanitize-html';
import htmlCompare from 'html-compare';
import { canUseDOM } from './markdown.plugin.utils.js';

const sanitizeHtml = html => {
  const integer = '[0-9]+';
  const number = "".concat(integer, "(.").concat(integer, ")*");
  const percentage = "".concat(number, "%");
  const units = '(?:em|pt|pc|px)';
  const length = "".concat(number).concat(units, "?");
  const lenOrPer = "(?:".concat(length, "|").concat(percentage, ")");
  const positions = /^(?:static|relative|absolute|sticky|fixed)$/i;
  const familyName = '[\\w]+(?:[ -]?\\w+)*';
  const genericFont = '(?:serif|sans-serif|cursive|fantasy|monospace)';
  const lineStyle = '(?:none|hidden|dotted|dashed|solid|double|groove|ridge|inset|outset)';
  const lineWidth = "(?:thin|medium|thick|(".concat(length, "))");
  const lenOrPerRE = new RegExp("^".concat(lenOrPer, "$"), 'i');
  // allow color words, and regex of 3 or 6 characters
  const colorOrHex = [new RegExp('^[a-z]+$', 'i'), new RegExp('^#(?:[0-9a-f]{3}){1,2}$', 'i')];
  const sanitizedHtml = sanitizeHtmlLibrary(html, {
    allowedTags: sanitizeHtmlLibrary.defaults.allowedTags.concat(['img', 'input', 's', 'blockquote', 'h1', 'h2', 'kbd', 'center', 'span', 'br',
    // needed for react-intl strings
    'video' // needed for plugins/videoServices/CustomVideoService.js
    ]),
    allowedAttributes: {
      '*': ['aria-*', 'class'],
      div: ['data-reactroot', 'id'],
      a: [{
        name: 'target',
        values: '_blank'
      }, 'href', 'id', 'title'],
      i: ['style', {
        name: 'role',
        values: ['img']
      }],
      img: ['src', 'alt', 'title'],
      h1: ['id'],
      h2: ['id'],
      h3: ['id'],
      h4: ['id'],
      h5: ['id'],
      h6: ['id'],
      ol: ['start'],
      li: ['value'],
      input: [{
        name: 'class',
        values: ['inline-code']
      }, {
        name: 'type',
        values: ['text']
      }, 'readonly', 'value', 'size'],
      th: ['style'],
      td: ['style'],
      span: ['style', 'id', {
        name: 'role',
        values: ['img']
      }],
      iframe: [{
        name: 'type',
        values: ['text/html']
      }, 'src', 'frameborder', 'width', 'height', 'webkitallowfullscreen', 'mozallowfullscreen', 'allowfullscreen'],
      video: ['id', 'width', 'height', 'controls'],
      table: ['tabindex']
    },
    allowedStyles: {
      th: {
        'text-align': [/^right$/]
      },
      td: {
        'text-align': [/^right$/]
      },
      i: {
        color: colorOrHex
      },
      span: {
        'background-color': colorOrHex,
        'border-color': colorOrHex,
        'border-radius': [new RegExp("^".concat(lenOrPer, "( ").concat(lenOrPer, "){0,3}$"), 'i')],
        'border-style': [new RegExp("^".concat(lineStyle, "( ").concat(lineStyle, "){0,3}$"), 'i')],
        'border-width': [new RegExp("^".concat(lineWidth, "( ").concat(lineWidth, "){0,3}$"), 'i')],
        color: colorOrHex,
        'font-family': [new RegExp("^".concat(genericFont, "$"), 'i'), new RegExp("^".concat(familyName, "$"), 'i'), new RegExp("^(".concat(familyName, ", ?)+").concat(genericFont, "$"), 'i')],
        'font-size': [/^(?:xx-small|x-small|small|medium|large|x-large|xx-large)$/i, /^(?:larger|smaller)$/i, lenOrPerRE],
        'font-weight': [new RegExp("^(?:normal|bold|bolder|lighter|(".concat(number, "))"), 'i')],
        'outline-color': colorOrHex,
        padding: [new RegExp("^".concat(lenOrPer, "( ").concat(lenOrPer, "){0,3}$"), 'i')],
        'padding-bottom': [lenOrPerRE],
        'padding-left': [lenOrPerRE],
        'padding-right': [lenOrPerRE],
        'padding-top': [lenOrPerRE],
        position: [positions],
        top: [new RegExp("^-?".concat(length, "$")), new RegExp("^-?".concat(percentage, "$")), /^auto$/i],
        'white-space': [/^(?:normal|pre|nowrap|pre-wrap|pre-line)$/i]
      }
    },
    allowProtocolRelative: true,
    allowIframeRelativeUrls: false,
    allowedIframeHostnames: ['www.youtube.com', 'player.vimeo.com', 'prezi.com', 'player.twitch.tv']
  });
  const createElementFromHTML = htmlString => {
    const div = document.createElement('div');
    div.innerHTML = htmlString.trim();
    return div;
  };
  const removeStyleAttribute = currentNode => {
    // We have issues comparing this attribute
    // So we will remove it for the Full STOP exception test
    // However, it will still go through sanitation

    // These are equivalent, but fails node comparison
    // <i style="text-align: right">
    // <i style="text-align: right;">
    // <i style="text-align:right">
    // <i style="text-align:right;">

    if (currentNode && currentNode.hasAttribute('style')) currentNode.removeAttribute('style');
    for (let n = 0; n < currentNode.childNodes.length; n++) {
      const child = currentNode.childNodes[n];
      if (child.nodeType === 1)
        // node type is element; recursively remove style attribute
        removeStyleAttribute(child);
    }
  };

  // non-browser env comparison
  // html-compare library diffs html strings, returns result object
  if (!canUseDOM) {
    // remove style attribute from html strings
    const removeStyle = str => {
      return str.replace(/(<[^>]+) style=".*?"/gi, '$1');
    };
    const result = htmlCompare.compare(removeStyle(html), removeStyle(sanitizedHtml));
    if (result.different) {
      // eslint-disable-next-line no-console
      console.log(html);
      // eslint-disable-next-line no-console
      console.log(sanitizedHtml);
      throw new Error('Disallowed HTML detected! Full STOP!');
    }
    return sanitizedHtml;
  }

  /**
  *  A requirement of AppSec is that we hard stop
  * when we find sanitation has cleaned something
  * There are no hooks in sanitize-html, so we have to compare
  * the input and output.
   * Because the sanitation cleans properties and outputs
  * corrected XHTML, we have to test using node tree equivalency
  */
  const origElem = createElementFromHTML(html);
  const sanitizedElem = createElementFromHTML(sanitizedHtml);

  /**
   * We also need to strip the CSS styles from our checks
   * because Node compare can't handle normalizing this field
   * during the comparison
   */
  removeStyleAttribute(origElem);
  removeStyleAttribute(sanitizedElem);
  if (!origElem.isEqualNode(sanitizedElem)) {
    // eslint-disable-next-line no-console
    console.log(html);
    // eslint-disable-next-line no-console
    console.log(sanitizedHtml);
    throw new Error('Disallowed HTML detected!  Full STOP!');
  }
  return sanitizedHtml;
};
var sanitizeHtml$1 = sanitizeHtml;

export { sanitizeHtml$1 as default, sanitizeHtml };
